import { type FC, Fragment } from 'react';
import { graphql, useFragment } from 'react-relay';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid.tsx';
import { useGeneratePhotoGrid } from '../hooks/useGeneratePhotoGrid.ts';
import RelatedPhotosGridTile from './RelatedPhotosGridTile.tsx';
import { PHOTO_MASONRY_PLACEMENT } from 'dibs-google-ad-manager/exports/constants.ts';
import { GridTileAd } from '../DisplayAds/GridTileAd.tsx';

import styles from './RelatedPhotosGrid.scss';

import { type RelatedPhotosGrid_photos$key } from './__generated__/RelatedPhotosGrid_photos.graphql.ts';

const COLUMNS_MAX = 12;

const COLUMNS_PER_PAGE = {
    DESKTOP: 4,
    MOBILE: 2,
};

//  Ad 1 - column 1 - position 4
//  Ad 2 - column 2 - position 10
//  Ad 3 - column 3 - position 16
// Indexing starts at 0 so -1 for ad, column
// -2 for position as its after the photo
// AD_MAP[columnIndex][position] = adIndex
const AD_MAP = {
    0: {
        2: 0,
    },
    1: {
        8: 1,
    },
    2: {
        14: 2,
    },
};

type Props = {
    photoId: string;
    isMobile: boolean;
    photos: RelatedPhotosGrid_photos$key | null | undefined;
    isDesktop: boolean | undefined;
    areDisplayAdsEnabled: boolean | null | undefined;
};

const RelatedPhotosGrid: FC<Props> = ({
    isMobile,
    photoId,
    photos: photosRef,
    isDesktop,
    areDisplayAdsEnabled,
}) => {
    const photos = useFragment(
        graphql`
            fragment RelatedPhotosGrid_photos on PhotosSearchBrowseQueryConnection {
                edges {
                    node {
                        serviceId
                        ...RelatedPhotosGridTile_photo
                    }
                }
            }
        `,
        photosRef
    );

    const columnsPerPage = isMobile ? COLUMNS_PER_PAGE.MOBILE : COLUMNS_PER_PAGE.DESKTOP;

    const { photoGrid } = useGeneratePhotoGrid({
        columnsPerPage,
        photoEdges: photos?.edges,
        mainPhotoId: photoId,
    });

    const mobileCol = COLUMNS_MAX / columnsPerPage;

    return (
        <div className={styles.wrapper}>
            <GridRow>
                {photoGrid.map((column, columnIndex) => (
                    <GridCol mobile={mobileCol} key={`photo-column-${columnIndex}`}>
                        {column.map((photo, index) => {
                            const key = photo.serviceId || `${columnIndex}-${index}`;
                            return (
                                <Fragment key={key}>
                                    <RelatedPhotosGridTile photo={photo} isMobile={isMobile} />
                                    <GridTileAd
                                        adMap={AD_MAP}
                                        columnIndex={columnIndex}
                                        index={index}
                                        placement={PHOTO_MASONRY_PLACEMENT}
                                        isDesktop={isDesktop}
                                        areDisplayAdsEnabled={areDisplayAdsEnabled}
                                    />
                                </Fragment>
                            );
                        })}
                    </GridCol>
                ))}
            </GridRow>
        </div>
    );
};

export default RelatedPhotosGrid;
